import { createContext, FunctionComponent, useContext, useMemo } from "react";
import { useAppSelector } from "../redux/hook";
import { useGetCpfServiceNotifiedStatusQuery, useGetCpfWithdrawalOverviewQuery } from "../redux/services/getStageE";
import { PolymerCrypto } from "../utils/encryptor";

export const StageEDetailsContext = createContext({} as any);

// Simplify Access to Context
export const useStageEContext = () => {
    const context = useContext(StageEDetailsContext)
    if (context === undefined) {
        throw new Error('useMyCtx must be used withing a Provider')
    }
    return context
}

interface StageEProviderPropsType {
    children: React.ReactNode;
}

export const StageEContextProvider = ({ children }: StageEProviderPropsType) => {
    const queryParameters = new URLSearchParams(window.location.search);
    const application_id = queryParameters.get("application_id");
    const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
    const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
    const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);

    const { data: proposedOwnersNotifiedStatus, isSuccess: isStageEDataSuccess,
        isFetching: isStageEDataFetching, refetch: stageERefetch
    } = useGetCpfServiceNotifiedStatusQuery({ application_id: application_id ?? "0", uinfin: loginUser.uinfin, singpass_token: singpassToken ?? "0" });

    // Start -- New Context
    const { data: cpfWithdrawalOverview
    } = useGetCpfWithdrawalOverviewQuery({ application_id: application_id ?? "0", uinfin: loginUser.uinfin ?? "", singpass_token: singpassToken ?? "0" });

    const stageELoginOwnerDetails = (() => {
        // Find user details in incoming_owners OR owners
        const loggedInOwnerStep1Details =
            applicationStep1FormData?.household?.incoming_owners?.find(
                (owner) => owner?.myinfo?.uinfin === loginUser?.uinfin
            ) ??
            applicationStep1FormData.household.owners.find(
                (owner) => owner?.myinfo?.uinfin === loginUser?.uinfin
            );

        // Filter CPF details for the logged-in user
        const loggedInOwnerCpfDetails = cpfWithdrawalOverview?.parties?.filter(
            (item: any) => item?.nric === loginUser?.uinfin
        );

        // Find notified status (decrypt uinfin)
        const loggedInOwnerNotifiedStatus = proposedOwnersNotifiedStatus?.find(
            (ownerDeets: any) =>
                PolymerCrypto.decryption(ownerDeets?.uinfin) === loginUser?.uinfin
        );

        // Consolidate and return the result
        return {
            ...loggedInOwnerNotifiedStatus,
            ...loggedInOwnerStep1Details,
            cpf_details: [...(loggedInOwnerCpfDetails || [])],
        };
    })();

    const stageEotherOwnerDetails = (() => {
        // Start with CPF Withdrawal Overview as the base and exclude the logged-in user
        const otherOwnersBase = cpfWithdrawalOverview?.parties?.filter(
            (cpfItem: { nric: string; }) => cpfItem?.nric !== loginUser?.uinfin
        );

        // Map over otherOwnersBase to enrich with Step1 details and notification status
        const enrichedOtherOwners = otherOwnersBase?.map((cpfItem: { nric: string; }) => {
            // Match Step1 details from incoming_owners or owners
            const otherOwnerStep1Details =
                applicationStep1FormData?.household?.incoming_owners?.find(
                    (incomingOwner) =>
                        incomingOwner?.myinfo?.uinfin === cpfItem?.nric
                ) ??
                applicationStep1FormData?.household?.owners?.find(
                    (owner) => owner?.myinfo?.uinfin === cpfItem?.nric
                );

            // Find notified status from proposedOwnersNotifiedStatus
            const otherOwnerNotifiedStatus = proposedOwnersNotifiedStatus?.find(
                (ownerDeets: { uinfin: string; }) =>
                    PolymerCrypto?.decryption(ownerDeets?.uinfin) === cpfItem?.nric
            );

            // Return consolidated owner details
            return {
                ...cpfItem, // Start with CPF Withdrawal details
                ...otherOwnerStep1Details, // Merge Step1 details
                ...otherOwnerNotifiedStatus, // Merge notified status
                hasNotification: !!otherOwnerNotifiedStatus, // Flag for notification
            };
        });

        return enrichedOtherOwners || []; // Return empty array if undefined
    })();

    return (
        <StageEDetailsContext.Provider value={{ stageELoginOwnerDetails, stageEotherOwnerDetails, isStageEDataSuccess, isStageEDataFetching, stageERefetch }}>
            {children}
        </StageEDetailsContext.Provider>
    );
};

// Higher-order component to inject context into existing top-level component
export const withContext = <T extends {}>(Component: FunctionComponent<T>) => {
    return (props: T) => {
        return (
            <StageEContextProvider>
                <Component {...props} />
            </StageEContextProvider>
        );
    };
};